import { FoodsIcon, SecondaryTab, SecondaryTabs } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate } from "react-router";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { useScoreDetailContext } from "src/context/ScoreDetail/useScoreDetailContext";
import { Box } from "@mui/material";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { ReactElement } from "react";

export type TabKeys = "distribution" | "comparison" | "evolution";
export type Tabs = TabKeys | null;

export type TabConfig = {
    distribution: TabButton;
    comparison?: TabButton;
    evolution?: TabButton;
};

type TabButton = {
    name?: string;
    icon?: ReactElement;
    link: (scoreId: string | number) => string;
};

type ScoreDetailTabsProps = {
    selectedTab: Tabs;
    tabOrder?: readonly TabKeys[];
    ignoreFeatureFlags?: boolean;
};
export const ScoreDetailTabs = (props: ScoreDetailTabsProps) => {
    const { selectedTab, tabOrder = ["distribution", "comparison", "evolution"], ignoreFeatureFlags = false } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isFeatureActivated } = useApplicationParameters();
    const { score, olapScores, drilldownState, tabConfig } = useScoreDetailContext();
    const { entity } = useEntityScores();

    const featureFlags: Record<TabKeys, string> = {
        distribution: `FS-${entity}-score-details-distribution`,
        comparison: `FS-${entity}-score-details-comparison`,
        evolution: "FS-score-details-evolution",
    };
    const defaultNames: Record<TabKeys, string> = {
        distribution: t("distribution_variant"),
        comparison: t("distribution_comparison"),
        evolution: t("evolution", "Evolution"),
    };
    const defaultIcons: Record<TabKeys, ReactElement> = {
        distribution: <FoodsIcon size={2} icon="repartition" />,
        comparison: <FoodsIcon size={2} icon="comparison" />,
        evolution: <FoodsIcon size={2} icon="evolution" />,
    };

    const visibleTabs = tabOrder.flatMap((key): { key: TabKeys; tab: SecondaryTab }[] => {
        const item = tabConfig[key];
        const isVisible = item !== undefined && (ignoreFeatureFlags || isFeatureActivated(featureFlags[key]));
        if (!isVisible) return [];

        return [
            {
                key,
                tab: {
                    name: item.name ?? defaultNames[key],
                    component: <Outlet />,
                    onClick: () => {
                        if (olapScores === undefined) {
                            drilldownState.reset();
                        }

                        navigate(item.link(score.id));
                    },
                    icon: item.icon ?? defaultIcons[key],
                },
            },
        ];
    });

    const selectedTabIndex = Math.max(
        0,
        selectedTab === null ? 0 : visibleTabs.findIndex(({ key }) => key === selectedTab),
    );

    return (
        <Box
            sx={(theme) => ({
                "& .secondary-tabs-wrapper": {
                    [theme.breakpoints.down("md")]: {
                        border: "none",
                        borderRadius: 0,
                    },
                },
            })}
        >
            <SecondaryTabs
                tabs={visibleTabs.map(({ tab }) => tab)}
                selectedTabIndex={selectedTabIndex}
                // This logic would need to be refined, but works today
                // selectedTab={(tab) => tab.id === selectedTabId}
            />
        </Box>
    );
};
